import { t } from "ttag";

export type SyncAction = "push" | "pull";

export interface SyncJob {
  entity: string;
  action: SyncAction;
}

export interface SyncTab {
  id: string;
  label: string;
  entities: string[];
}

export const SHOW_BACNET_TEMPLATES = false;

const SYNC_TAB_ENTITIES: Array<{ id: string; entities: string[] }> = [
  { id: "hierarchy", entities: ["hierarchy"] },
  { id: "schedules", entities: ["schedules"] },
  { id: "interlockRules", entities: ["interlockRules"] },
  { id: "changeoverRules", entities: ["changeoverRules"] },
  { id: "dualSetpointRules", entities: ["dualSetpointRules"] },
  { id: "autoTimerRules", entities: ["autoTimerRules"] },
  { id: "bacnetDevices", entities: ["bacnetDevices"] },
  { id: "bacnetUnits", entities: ["bacnetUnits"] },
  { id: "modbusDevices", entities: ["modbusDevices"] },
  { id: "modbusUnits", entities: ["modbusUnits"] },
  ...(SHOW_BACNET_TEMPLATES ? [{ id: "bacnetTemplates", entities: ["bacnetTemplates"] }] : [])
];

// ttag resolves against the locale that is active when the tag runs, so every
// translated label lives behind a call rather than a module-level constant.
const ENTITY_LABELS: Record<string, () => string> = {
  hierarchy: () => t`Hierarchy`,
  schedules: () => t`Schedules`,
  interlockRules: () => t`Interlock Rules`,
  changeoverRules: () => t`Auto Changeover`,
  dualSetpointRules: () => t`Dual Setpoint`,
  autoTimerRules: () => t`Auto Timer`,
  bacnetDevices: () => t`BACnet Devices`,
  bacnetUnits: () => t`BACnet Units`,
  bacnetTemplates: () => t`BACnet Templates`,
  modbusDevices: () => t`Modbus Devices`,
  modbusUnits: () => t`Modbus Units`
};

const HIERARCHY_SUB_LABELS: Array<{ key: string; label: () => string }> = [
  { key: "groups", label: () => t`Groups` },
  { key: "systems", label: () => t`Systems` },
  { key: "controlUnits", label: () => t`Control units` },
  { key: "serviceUnits", label: () => t`Service units` },
  { key: "outdoorUnits", label: () => t`Outdoor units` },
  { key: "compressors", label: () => t`Compressors` },
  { key: "sensors", label: () => t`Sensors` },
  { key: "powerMeters", label: () => t`Power meters` }
];

const UNSUPPORTED_ACTIONS: Record<string, Partial<Record<SyncAction, () => string>>> = {
  bacnetDevices: {
    push: () => t`Push (cloud → device) is not supported for BACnet devices`
  },
  interlockRules: {
    pull: () => t`Pull (device → cloud) is not supported for Interlock Rules`
  },
  bacnetTemplates: {
    pull: () => t`Pull (device → cloud) is not supported for BACnet templates`
  }
};

export const ALL_SYNC_ENTITIES = SYNC_TAB_ENTITIES.flatMap((tab) => tab.entities);

export function getEntityLabel(entity: string): string {
  return ENTITY_LABELS[entity]?.() || entity;
}

export function getSyncTabs(): SyncTab[] {
  return SYNC_TAB_ENTITIES.map((tab) => ({ ...tab, label: getEntityLabel(tab.id) }));
}

export function getHierarchySubEntities(): Array<{ key: string; label: string }> {
  return HIERARCHY_SUB_LABELS.map((item) => ({ key: item.key, label: item.label() }));
}

export function deviceDisconnectedReason(): string {
  return t`Device is disconnected`;
}

export function entitiesForValidate(includeTemplates: boolean): string[] {
  return includeTemplates
    ? ALL_SYNC_ENTITIES
    : ALL_SYNC_ENTITIES.filter((entity) => entity !== "bacnetTemplates");
}

export function tabIndexForEntity(entity: string): { tab: number; subTab: number } {
  const tab = SYNC_TAB_ENTITIES.findIndex((item) => item.entities.includes(entity));
  if (tab < 0) {
    return { tab: 0, subTab: 0 };
  }
  return { tab, subTab: Math.max(SYNC_TAB_ENTITIES[tab].entities.indexOf(entity), 0) };
}

export function isDeviceConnected(deviceOrFlag: unknown): boolean {
  const value =
    deviceOrFlag && typeof deviceOrFlag === "object"
      ? (deviceOrFlag as { isConnected?: unknown }).isConnected
      : deviceOrFlag;
  return value === true || value === 1 || value === "true" || value === "1";
}

export function getUnsupportedSyncReason(entity: string, action: SyncAction): string | null {
  return UNSUPPORTED_ACTIONS[entity]?.[action]?.() || null;
}

export function fetchButtonDisableReason(isConnected = true): string | null {
  return isConnected ? null : deviceDisconnectedReason();
}

export function directionSyncDisableReason(isConnected = true): string | null {
  return isConnected ? null : deviceDisconnectedReason();
}

export function syncButtonDisableReason(
  entity: string,
  action: SyncAction,
  status: "pending" | "unavailable" | "pass" | "fail",
  isConnected = true
): string | null {
  if (!isConnected) {
    return deviceDisconnectedReason();
  }
  return getUnsupportedSyncReason(entity, action)
    || (status === "pending" ? t`Waiting for compare` : null)
    || (status === "pass" ? t`Already matches — nothing to sync` : null);
}

export function collectDirectionSyncJobs(
  action: SyncAction,
  entities: string[] = ALL_SYNC_ENTITIES
): SyncJob[] {
  return entities
    .filter((entity) => !getUnsupportedSyncReason(entity, action))
    .map((entity) => ({ entity, action }));
}

export function collectSuggestedSyncJobs(
  items: Array<{
    entity: string;
    status: "pending" | "unavailable" | "pass" | "fail";
    missingOnDevice: boolean;
    missingInCloud: boolean;
  }>
): SyncJob[] {
  const jobs: SyncJob[] = [];
  for (const item of items) {
    if (item.missingOnDevice && !getUnsupportedSyncReason(item.entity, "push")) {
      jobs.push({ entity: item.entity, action: "push" });
    }
    if (item.missingInCloud && !getUnsupportedSyncReason(item.entity, "pull")) {
      jobs.push({ entity: item.entity, action: "pull" });
    }
  }
  return jobs;
}

export function isNgDevice(device: { deviceType?: unknown; type?: unknown } | null | undefined): boolean {
  return device?.deviceType == 1 && device?.type == 3;
}
